import LinkIcon from "@mui/icons-material/Link";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Autocomplete,
  Button,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import useProgramReviewUrlForm, {
  UseProgramReviewUrlFormProps,
  UseProgramReviewUrlFormReturnType,
} from "./useProgramReviewUrlForm";

export const ProgramReviewUrlContext = createContext<
  UseProgramReviewUrlFormReturnType | undefined
>(undefined);

export interface ProgramReviewUrlFormProps
  extends UseProgramReviewUrlFormProps {}

export default function ProgramReviewUrlForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: ProgramReviewUrlFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useProgramReviewUrlForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate
    ? I18n.t("ProgramReviewUrl.addTitle")
    : `${item.review_partner?.name} - ${item.program?.name}`;
  const subtitle = isCreate ? undefined : undefined;

  return (
    <ParamsContext.Provider
      value={{
        program_review_url_id: id,
        program_id: item.program_id,
        review_partner_id: item.review_partner_id,
      }}
    >
      <ProgramReviewUrlContext.Provider value={form}>
        <PageHeader
          avatar={<LinkIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="ProgramReviewUrl.attributes"
            translationPlaceholdersCategory="ProgramReviewUrl.placeholders"
            translationHelpersCategory="ProgramReviewUrl.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("program_review_url", item)}
          >
            <FieldsContainer>
              {!params?.review_partner_id && (
                <Field fullWidth>
                  <Autocomplete id="review_partner_id" />
                </Field>
              )}
              {!params?.program_id && (
                <Field fullWidth>
                  <Autocomplete id="program_id" />
                </Field>
              )}
              <Field fullWidth>
                <Autocomplete
                  id="lang"
                  options={[
                    { value: "en", label: "Angol" },
                    { value: "hu", label: "Magyar" },
                  ]}
                />
              </Field>
              <Field fullWidth>
                <TextField id="url" />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="ProgramReviewUrl"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </ProgramReviewUrlContext.Provider>
    </ParamsContext.Provider>
  );
}
